/**
 * Done. / ChiroCPD — ActivityForm Feature Component
 *
 * 2-col desktop / 1-col mobile form for logging CPD activities.
 * beforeunload guard via useUnsavedChanges.
 * ErrorSummary on failed submit, focus moved to first error.
 * Hours cap guard — warns when remaining hours would be exceeded.
 *
 * URL-scrape agent: if a ?url= query param is present (e.g. handoff from
 * the Dashboard's External CPD card), the form auto-fires
 * POST /api/ai/scrape-url and pre-fills title/description/hours + remembers
 * the source URL on save. The AI-drafted outcome/reflection is shown as a
 * preview the practitioner can copy after saving.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { AILabel } from '@/components/ui/AILabel';
import { ErrorSummary } from '@/components/ui/ErrorSummary';
import { CapIndicator } from '@/components/ui/CapIndicator';
import { useCapStatus } from '@/hooks/useCapStatus';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { api, ApiError } from '@/lib/api';
import { todayIso } from '@/lib/utils';
import type {
  ActivityType,
  CreateActivityInput,
  FormError,
  ScrapeUrlResult,
} from '@/lib/types';

interface ActivityFormProps {
  activityTypes: ActivityType[];
  onSave: (input: CreateActivityInput) => Promise<void>;
  onCancel: () => void;
  editingActivity?: { activityTypeId: string; title: string; description: string | null; hours: number; activityDate: string } | null;
  /** Pre-fill URL from caller (e.g. ?url= query param on /activities). */
  initialUrl?: string | null;
}

interface FormValues {
  activityTypeId: string;
  title: string;
  description: string;
  hours: string;
  activityDate: string;
}

const EMPTY_FORM: FormValues = {
  activityTypeId: '',
  title: '',
  description: '',
  hours: '',
  activityDate: todayIso(),
};

function validate(values: FormValues): FormError[] {
  const errors: FormError[] = [];
  if (!values.activityTypeId) {
    errors.push({ field: 'activityTypeId', message: 'Activity type is required' });
  }
  if (!values.title.trim()) {
    errors.push({ field: 'title', message: 'Title is required' });
  }
  const hours = parseFloat(values.hours);
  if (isNaN(hours) || hours <= 0) {
    errors.push({ field: 'hours', message: 'Hours must be a positive number' });
  }
  if (hours > 20) {
    errors.push({ field: 'hours', message: 'A single activity cannot exceed 20 hours' });
  }
  if (!values.activityDate) {
    errors.push({ field: 'activityDate', message: 'Date is required' });
  }
  return errors;
}

export function ActivityForm({
  activityTypes,
  onSave,
  onCancel,
  editingActivity,
  initialUrl,
}: ActivityFormProps): React.ReactElement {
  const initialValues: FormValues = editingActivity
    ? {
        activityTypeId: editingActivity.activityTypeId,
        title: editingActivity.title,
        description: editingActivity.description ?? '',
        hours: String(editingActivity.hours),
        activityDate: editingActivity.activityDate,
      }
    : EMPTY_FORM;
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<FormError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { cap } = useCapStatus();

  // URL scrape state
  const [urlInput, setUrlInput] = useState(initialUrl ?? '');
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ScrapeUrlResult | null>(null);
  const autoFiredRef = useRef(false);

  const isDirty =
    values.activityTypeId !== EMPTY_FORM.activityTypeId ||
    values.title !== EMPTY_FORM.title ||
    values.hours !== EMPTY_FORM.hours;

  useUnsavedChanges(isDirty);

  function set(field: keyof FormValues, value: string): void {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => prev.filter(e => e.field !== field));
  }

  const runScrape = useCallback(async (url: string): Promise<void> => {
    setScraping(true);
    setScrapeError(null);
    try {
      const result = await api.post<ScrapeUrlResult>('/api/ai/scrape-url', { url });
      setDraft(result);
      setSourceUrl(result.sourceUrl);
      setValues(prev => ({
        ...prev,
        title: result.suggestedTitle || prev.title,
        description: result.suggestedDescription || prev.description,
        hours: result.suggestedHours > 0 ? String(result.suggestedHours) : prev.hours,
      }));
    } catch (err) {
      if (err instanceof ApiError) {
        setScrapeError(err.message);
      } else if (err instanceof Error) {
        setScrapeError(err.message);
      } else {
        setScrapeError('Could not read the page. Please add the activity manually.');
      }
    } finally {
      setScraping(false);
    }
  }, []);

  // Auto-fire scrape if caller passed an initial URL.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (!initialUrl) return;
    if (editingActivity) return;
    autoFiredRef.current = true;
    void runScrape(initialUrl);
  }, [initialUrl, editingActivity, runScrape]);

  const handleSubmit = useCallback(async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const validationErrors = validate(values);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors([]);
    try {
      await onSave({
        activityTypeId: values.activityTypeId,
        title: values.title.trim(),
        description: values.description.trim() || null,
        hours: parseFloat(values.hours),
        activityDate: values.activityDate,
        sourceUrl,
      });
      setValues(EMPTY_FORM);
      setDraft(null);
      setSourceUrl(null);
      setUrlInput('');
    } catch (err) {
      setErrors([{
        field: 'submit',
        message: err instanceof Error ? err.message : 'Failed to save activity. Please try again.',
      }]);
    } finally {
      setSubmitting(false);
    }
  }, [values, onSave, sourceUrl]);

  return (
    <form
      onSubmit={e => { void handleSubmit(e); }}
      noValidate
      className="rounded-[var(--radius-lg)] border border-[var(--border-base)] bg-[var(--surface-base)] p-6 shadow-sm"
      aria-label="Log CPD activity"
    >
      <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4">Log Activity</h2>

      {/* ── URL-scrape agent panel ─────────────────────────────────────── */}
      {!editingActivity && (
        <div className="mb-6 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                Generate from URL <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
              </h3>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">
                Paste a conference, webinar, or podcast link. We&apos;ll read the page and pre-fill this form
                with a suggested title, summary, hours, and a draft Outcome/Reflection.
              </p>
            </div>
            <AILabel />
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={e => { setUrlInput(e.target.value); }}
              placeholder="https://example.com/conference-programme"
              className="flex-1 rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)]"
              aria-label="Activity URL"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => { void runScrape(urlInput.trim()); }}
              ariaDisabled={scraping || urlInput.trim().length === 0}
            >
              {scraping ? 'Reading…' : 'Generate from URL'}
            </Button>
          </div>
          {scrapeError && (
            <p
              role="alert"
              className="mt-3 rounded-[var(--radius-sm)] bg-[var(--color-error-100,#fee2e2)] px-3 py-2 text-xs text-red-700"
            >
              {scrapeError}
            </p>
          )}
          {draft && (
            <details className="mt-3 group">
              <summary className="cursor-pointer text-xs font-medium text-[var(--color-done-600,#0f7f75)] hover:underline">
                AI-drafted Outcome &amp; Reflection — click to preview
              </summary>
              <div className="mt-3 space-y-3 rounded-[var(--radius-sm)] bg-[var(--surface-base)] border border-[var(--border-subtle)] p-3">
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1">Outcome draft</p>
                  <p className="text-sm whitespace-pre-wrap text-[var(--text-primary)]">{draft.outcomeText}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1">Reflection draft</p>
                  <p className="text-sm whitespace-pre-wrap text-[var(--text-primary)]">{draft.reflectionText}</p>
                </div>
                <p className="text-xs text-[var(--text-muted)] italic">
                  After saving the activity, open it from the list and click &ldquo;Generate Reflection&rdquo; to apply
                  this draft to the Board portal fields. {draft.contentTruncated ? 'Page content was truncated for length.' : ''}
                </p>
              </div>
            </details>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <div className="mb-4">
          <ErrorSummary errors={errors} />
        </div>
      )}

      {cap && (
        <div className="mb-4">
          <CapIndicator
            current={cap.annualHoursLogged}
            max={cap.annualHourCap}
            unit="hours"
            variant="inline"
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="activityTypeId"
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            Activity type
            <span className="text-[var(--color-error-500)] ml-1" aria-hidden="true">*</span>
          </label>
          <select
            id="activityTypeId"
            value={values.activityTypeId}
            onChange={e => { set('activityTypeId', e.target.value); }}
            required
            aria-required="true"
            aria-invalid={errors.some(e => e.field === 'activityTypeId') ? 'true' : undefined}
            className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm min-h-[44px] bg-[var(--surface-base)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)]"
          >
            <option value="">Select type…</option>
            {activityTypes.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {errors.find(e => e.field === 'activityTypeId') && (
            <p className="text-xs text-[var(--color-error-500)]" role="alert">
              {errors.find(e => e.field === 'activityTypeId')?.message}
            </p>
          )}
        </div>

        <Input
          id="activityDate"
          label="Activity date"
          type="date"
          value={values.activityDate}
          onChange={v => { set('activityDate', v); }}
          required
          max={todayIso()}
          error={errors.find(e => e.field === 'activityDate')?.message}
        />

        <div className="sm:col-span-2">
          <Input
            id="title"
            label="Title"
            value={values.title}
            onChange={v => { set('title', v); }}
            required
            placeholder="e.g. NZ Chiropractic Conference 2026"
            error={errors.find(e => e.field === 'title')?.message}
          />
        </div>

        <Input
          id="hours"
          label="Duration (hours)"
          type="number"
          value={values.hours}
          onChange={v => { set('hours', v); }}
          required
          min={0.5}
          max={20}
          step={0.5}
          placeholder="e.g. 4.0"
          error={errors.find(e => e.field === 'hours')?.message}
        />

        <div className="sm:col-span-2 flex flex-col gap-1">
          <label
            htmlFor="description"
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            Description
            <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
          </label>
          <textarea
            id="description"
            value={values.description}
            onChange={e => { set('description', e.target.value); }}
            rows={3}
            placeholder="Brief description of the activity content…"
            className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
          />
        </div>

        {sourceUrl && (
          <div className="sm:col-span-2 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <span aria-hidden="true">🔗</span>
            <span>
              Source URL on save: <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-[var(--color-done-600,#0f7f75)] hover:underline">{sourceUrl}</a>
            </span>
            <button
              type="button"
              onClick={() => { setSourceUrl(null); }}
              className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              aria-label="Remove source URL"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          ariaDisabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save Activity'}
        </Button>
      </div>
    </form>
  );
}
